/**
 * The recall detail's decisions, as values — `screens.md` §11, plan U49.
 *
 * The trail renders in the order `recalls.read` returns it, which is `order by seq`
 * (`apps/api/src/ops/recalls.ts`, `readRecall`). Nothing here sorts it.
 */
import {
  recallScopeSchema,
  type RecallDetail,
  type RecallScope,
} from "@packagepage/shared";

import { actorLabel, auditTimestamp } from "./audit-log.js";
import { splitLots } from "./recall-flow.js";

export const WIDEN_GTIN_NOTE = "Nothing that already sees the notice stops seeing it.";

export const LIFT_CONSEQUENCE =
  "Scans will return to their normal destinations. The product page goes back to exactly " +
  "how it was — no banner, no marker, nothing left over.";

/** The design's lift copy, with the scan count read from `reach`. */
export function liftRecord(scans: number): string {
  return (
    `The notice and its ${count(scans, "reached scan", "reached scans")} stay in your ` +
    "evidence record, which you can still download here after the lift. You can publish " +
    "a new notice in under a minute if you need to."
  );
}

export const EVIDENCE_NOTE =
  "The notice, the scans it reached and every change in the order it happened — for a " +
  "regulator, a retailer or an insurer. Still here after the lift.";

type RecallEvent = RecallDetail["events"][number];

function listed(lots: readonly string[]): string {
  if (lots.length <= 1) return lots[0] ?? "";
  return `${lots.slice(0, -1).join(", ")} and ${lots[lots.length - 1] as string}`;
}

export function scopeLabel(scope: RecallScope | string, lots: readonly string[]): string {
  return scope === "gtin" ? "every unit of this product" : listed(lots);
}

export function liftQuestion(scope: RecallScope, lots: readonly string[]): string {
  return scope === "gtin"
    ? "Lift the recall notice on every unit of this product?"
    : `Lift the recall notice on ${listed(lots)}?`;
}

/** `2026-10-20 14:32 UTC`, from `audit-log.ts`'s fixed format. */
export function when(iso: string): string {
  return `${auditTimestamp(iso)} UTC`;
}

export function firstOf(
  events: readonly RecallEvent[],
  kind: string,
): RecallEvent | undefined {
  return events.find((event) => event.kind === kind);
}

const KIND_LABEL: Readonly<Record<string, string>> = {
  published: "Published",
  scope_widened: "Scope widened",
  lifted: "Lifted",
  restore_reinstated: "Brand defaults reinstated",
};

const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

const count = (n: number, one: string, many: string) =>
  `${String(n)} ${n === 1 ? one : many}`;

/** One sentence per event, read from its payload. An unknown kind keeps its raw token. */
export function eventSummary(kind: string, payload: unknown): string {
  const p = record(payload);
  switch (kind) {
    case "published": {
      const suppressed = strings(p["suppressed"]).length;
      return (
        `Scoped to ${scopeLabel(String(p["scope"]), strings(p["lots"]))}. ` +
        `${count(suppressed, "brand default", "brand defaults")} suspended for the duration.`
      );
    }
    case "scope_widened": {
      const from = record(p["from"]);
      const to = record(p["to"]);
      const added = strings(p["addedLots"]);
      const toText =
        to["scope"] === "gtin"
          ? "the whole product"
          : scopeLabel(String(to["scope"]), strings(to["lots"]));
      return (
        `From ${scopeLabel(String(from["scope"]), strings(from["lots"]))} to ${toText}.` +
        (added.length === 0 ? "" : ` Added ${listed(added)}.`)
      );
    }
    case "lifted": {
      const deleted = typeof p["linksDeleted"] === "number" ? p["linksDeleted"] : 0;
      return `${count(deleted, "notice link", "notice links")} removed.`;
    }
    case "restore_reinstated": {
      const captured = typeof p["captured"] === "number" ? p["captured"] : 0;
      const reinstated = strings(p["reinstated"]).length;
      const skipped = Array.isArray(p["skipped"]) ? p["skipped"].length : 0;
      return (
        `${String(reinstated)} of ${count(captured, "brand default", "brand defaults")} ` +
        `reinstated.` +
        (skipped === 0 ? "" : ` ${String(skipped)} could not be put back.`)
      );
    }
    default:
      return "";
  }
}

export interface TrailRow {
  readonly id: string;
  readonly kind: string;
  readonly label: string;
  readonly at: string;
  readonly when: string;
  readonly by: string;
  readonly summary: string;
}

/** The trail as rows, in the order given. */
export function trailRows(events: readonly RecallEvent[]): TrailRow[] {
  return events.map((event) => ({
    id: event.id,
    kind: event.kind,
    label: KIND_LABEL[event.kind] ?? event.kind,
    at: event.at,
    when: when(event.at),
    by: actorLabel(event.actor),
    summary: eventSummary(event.kind, event.payload),
  }));
}

/**
 * The widen body: the lots already covered plus the new ones, or the whole product.
 *
 * The op refuses a lots body that drops a covered lot (`RECALL_SCOPE_NARROWED`), so the
 * covered lots are always sent first and unchanged.
 */
export function widenBody(
  currentLots: readonly string[],
  change: { readonly to: "gtin" } | { readonly to: "lots"; readonly text: string },
): Record<string, unknown> {
  if (change.to === "gtin") return { scope: "gtin" };
  const added = splitLots(change.text).filter((lot) => !currentLots.includes(lot));
  return { scope: "lots", lots: [...currentLots, ...added] };
}

/** The lots a widen body adds to the current scope. */
export function addedLots(
  currentLots: readonly string[],
  body: Record<string, unknown>,
): string[] {
  return strings(body["lots"]).filter((lot) => !currentLots.includes(lot));
}

/** The op's own scope stage, before the call — Drift Log §U48. `null` means send it. */
export function widenPreflight(body: unknown): string | null {
  const parsed = recallScopeSchema.safeParse(body);
  return parsed.success
    ? null
    : (parsed.error.issues[0]?.message ?? "The request is not valid.");
}

export function widenOutcome(
  response: { widened: boolean; recall: { scope: string } },
  added: readonly string[],
): string {
  if (!response.widened)
    return "Every lot you named is already covered. Nothing was changed.";
  if (response.recall.scope === "gtin")
    return "The notice now covers every unit of this product. Live in about five seconds.";
  return `Added ${listed(added)}. Live in about five seconds.`;
}
